import * as Yup from 'yup';
import { isFuture } from 'date-fns';
import { ACCIDENT_DISCOVERER_IS, APPLICANT_IS } from '../../../containers/PropertyCompensationContainer/options';
import { EIGHT_DIGITS_REGEX, POLICY_PATTERN } from '../../../constants/patterns';
import { getLegalEntityValidator, getPhysicalEntityValidator } from '../claimApplicantDetailsValidation';
import accidentAddressSchema from '../accidentAddressSchema';

export const physicalPersonalDataSchema = Yup.object().shape({
  firstNameHY: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'hy' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  firstNameRU: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'ru' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  firstNameEN: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'en' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  lastNameHY: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'hy' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  lastNameRU: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'ru' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  lastNameEN: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'en' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  physAddressHY: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'hy' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  physAddressEN: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'en' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  passport: Yup.string()
    .min(3, 'validationErrors.wrongPassportNumber')
    .required('validationErrors.isRequired'),
  selectedProperties: Yup.array().min(1, 'validationErrors.isRequired'),
}).concat(Yup.object().shape(getPhysicalEntityValidator({
  genderKey: 'applicantGender',
  when: 'applicantIs',
  is: (applicantIs) => applicantIs === APPLICANT_IS.other.value,
}))).concat(Yup.object().shape(getLegalEntityValidator({
  when: 'applicantIs',
  is: (applicantIs) => applicantIs === APPLICANT_IS.otherLegal.value,
})));

export const legalPersonalDataSchema = Yup.object().shape({
  organizationNameHY: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'hy' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  organizationNameRU: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'ru' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  organizationNameEN: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'en' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  legalAddressHY: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'hy' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  legalAddressRU: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'ru' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  legalAddressEN: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === 'en' ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
  hvhh: Yup.string()
    .matches(EIGHT_DIGITS_REGEX, 'validationErrors.eightDigitsOnly')
    .required('validationErrors.isRequired'),
  selectedProperties: Yup.array().min(1, 'validationErrors.isRequired'),
}).concat(Yup.object().shape(getPhysicalEntityValidator({
  genderKey: 'applicantGender',
  when: 'applicantIs',
  is: (applicantIs) => applicantIs === APPLICANT_IS.other.value,
}))).concat(Yup.object().shape(getLegalEntityValidator({
  when: 'applicantIs',
  is: (applicantIs) => applicantIs === APPLICANT_IS.otherLegal.value,
})));

export const physicalApplicantSearchSchema = Yup.object().shape(
  {
    pnum: Yup.string().when(['ssn'], {
      is: (ssn) => !ssn,
      then: Yup.string()
        .min(3, 'searchApplicantErrors.passportNumMin')
        .required('searchApplicantErrors.passOrSsnRequired'),
      otherwise: Yup.string().test('pnum', 'searchApplicantErrors.passportNumMin', (value) => (value ? value.length === 0 || value.length >= 3 : true)),
    }),
    ssn: Yup.string().when(['pnum'], {
      is: (pnum) => !pnum,
      then: Yup.string()
        .min(3, 'searchApplicantErrors.passportNumMin')
        .test('ssn', 'searchApplicantErrors.onlyNums', (value) => (value ? /^\d*$/.test(value) : true))
        .required('searchApplicantErrors.ssnrOrPassRequired'),
      otherwise: Yup.string()
        .test('ssn', 'searchApplicantErrors.passportNumMin', (value) => (value ? value.length === 0 || value.length >= 3 : true))
        .test('ssn', 'searchApplicantErrors.onlyNums', (value) => (value ? /^\d*$/.test(value) : true)),
    }),
    policy: Yup.string()
      .required('validationErrors.isRequired')
      .test('policy', 'searchApplicantErrors.policyPattern', (value) => (value ? POLICY_PATTERN.test(value) : true)),
  },
  [['pnum', 'ssn']],
);

export const legalApplicantSearchSchema = Yup.object().shape({
  hvhh: Yup.string()
    .matches(/^\d{8}$/, 'validationErrors.eightDigitsOnly')
    .required('validationErrors.isRequired'),
  policy: Yup.string()
    .required('validationErrors.isRequired')
    .test('policy', 'searchApplicantErrors.policyPattern', (value) => (value ? POLICY_PATTERN.test(value) : true)),
});

export const accidentInfoSchema = Yup.object().shape({
  accidentDate: Yup.date()
    .nullable()
    .test('accidentDate', 'validationErrors.accDateCantBeFuture', (value) => !isFuture(value))
    .when(
      'startDate',
      (startDate, schem) => startDate && schem.min(startDate, 'validationErrors.notStartedPolicy'),
    )
    .when(
      'endDate',
      (endDate, schem) => endDate && schem.max(endDate, 'validationErrors.expiredPolicy'),
    )
    .required('validationErrors.isRequired'),
  accidentTime: Yup.string()
    .nullable()
    .required('validationErrors.isRequired'),
  damageAmount: Yup.number()
    .nullable()
    .typeError('validationErrors.onlyNumber')
    .max(200000000, 'validationErrors.maxNumber')
    .test('isZero', 'validationErrors.isRequired', (v) => v !== 0)
    .required('validationErrors.isRequired'),
  loanCompanyName: Yup.string().when('loanAgreementExists', {
    is: true,
    then: Yup.string()
      .required('validationErrors.isRequired')
      .isCorrectLanguage(),
  }),
  damageCauses: Yup.array()
    .min(1, 'validationErrors.isRequired')
    .required('validationErrors.isRequired'),
  damageCauseOther: Yup.string().when('damageCauses', (damageCauses = [], sch) => {
    if (damageCauses.some((dc) => dc.label === 'other')) {
      return sch.required('validationErrors.isRequired');
    }
    return sch;
  }),
  accidentDiscovererIs: Yup.string().required('validationErrors.isRequired'),
  accidentDiscovererOther: Yup.string()
    .isCorrectLanguage()
    .when('accidentDiscovererIs', (accidentDiscovererIs, sch) => {
      if (accidentDiscovererIs === ACCIDENT_DISCOVERER_IS.other.value) {
        return sch.required('validationErrors.isRequired');
      }
      return sch;
    }),
  organizations: Yup.array()
    .min(1, 'validationErrors.isRequired')
    .required('validationErrors.isRequired'),
  organizationOther: Yup.string().when('organizations', (organizations = [], sch) => {
    if (organizations.some((org) => org.label === 'other')) {
      return sch.required('validationErrors.isRequired').isCorrectLanguage();
    }
    return sch;
  }),
  accidentDetails: Yup.string()
    .nullable()
    .required('validationErrors.isRequired')
    .max(1000, 'validationErrors.maxNumber')
    .isCorrectLanguage(),
  accidentPrevention: Yup.string()
    .nullable()
    .max(1000, 'validationErrors.maxNumber')
    .isCorrectLanguage()
    .required('validationErrors.isRequired'),
}).concat(accidentAddressSchema);

export const getPropertySchema = (id) => Yup.object().shape({
  [`propertyName_${id}`]: Yup.string()
    .required('validationErrors.isRequired')
    .isCorrectLanguage(),
  [`propertyPrice_${id}`]: Yup.number()
    .moreThan(0, 'validationErrors.isRequired')
    .required('validationErrors.isRequired'),
  [`damageDescription_${id}`]: Yup.string()
    .required('validationErrors.isRequired')
    .isCorrectLanguage(),
  [`propertyYear_${id}`]: Yup.string()
    .matches(/^\d+$/, 'validationErrors.isRequired'),
});
